import type { ListBuilderConfiguration } from '../types';
import {
	createClientNameColumn,
	createContactInfoColumn,
	createLocationColumn,
} from './client/columns';
import { DEFAULT_LIST_REQUIRED_FIELDS } from './client/shared';

/**
 * PD-14186 — Ownership tab on the client detail page.
 *
 * A mini version of the main clients list (same shared columns: status dot +
 * linked name + address, type badge + business type + id, phone/email),
 * scoped server-side to the clients that share the viewed client's ownership
 * (the `_fk_ownership__id` context param, with `_fk_ownership__exclude_client`
 * dropping the viewed client itself). The actions column exposes "Remove" —
 * which clears that account's ownership via a confirmation modal in
 * ClientOwnershipAccountsTab (the client itself is never deleted). Ownership is
 * assigned from the client editor / the Manage Ownerships screen. The
 * `ownershipMembers` multi-select filter narrows the list to chosen members.
 */
export const CLIENT_OWNERSHIP_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	objectType: 'client',
	lists: [
		{
			id: 'client-ownership-all',
			cacheTTL: 600000,
			name: 'Ownership Accounts',
			type: 'default',
			active: true,
			columns: [
				createClientNameColumn(),
				createLocationColumn(),
				createContactInfoColumn(),
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'remove-ownership-action',
									label: 'Remove',
									iconName: 'link-slash',
									callbackName: 'removeOwnershipAccount',
									variant: 'danger',
									visibility: 'visible',
									visibilityCallback: 'shouldShowRemoveButton',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 70,
					align: 'center',
					sortable: false,
				},
			],
			description: 'Accounts that share this client’s ownership (PD-14186)',
			requiredFields: DEFAULT_LIST_REQUIRED_FIELDS,
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['clientSearch', 'ownershipMembers'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: '_id', fieldLabel: 'Internal ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'address1', fieldLabel: 'Address' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead/Customer' },
				{ fieldName: 'businessType', fieldLabel: 'Business Type' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort ownership accounts by name',
		},
		{
			id: 'name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort ownership accounts by name descending',
		},
		{
			id: 'type-asc',
			name: 'By Type',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'client.leadOrCustomer',
					objectTable: 'client',
				},
				{ direction: 'ASC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort by lead/customer then name',
		},
	],
};
